import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Calculator,
  ChevronDown,
  Coins,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Folders,
  Printer,
  RefreshCw,
  Save,
  Settings as SettingsIcon,
  Undo2,
} from 'lucide-react';
import { yearLabel } from '../lib/format';
import type { Settings } from '../types';

type Props = {
  settings: Settings;
  baseYear: number;
  onSettingsChange: (patch: Partial<Settings>) => void;
  onSave: () => void;
  onRefresh: () => void;
  onPrint: () => void;
  onCsv: () => void;
  onXlsx: () => void;
  onUndo: () => void;
  canUndo: boolean;
  undoDepth: number;
  dirty: boolean;
  busy: 'load' | 'save' | 'refresh' | null;
  hiddenCount: number;
  onAdmin?: () => void;
  onQuota?: () => void;
  onSections?: () => void;
  onCashflow?: () => void;
  /** Viewer mode hides save/sections and disables CPI sliders. The
   *  Quota button stays visible — viewers can still drive the
   *  calculator in-browser without persistence. */
  isViewer?: boolean;
};

export function ControlPanel({
  settings,
  baseYear,
  onSettingsChange,
  onSave,
  onRefresh,
  onPrint,
  onCsv,
  onXlsx,
  onUndo,
  canUndo,
  undoDepth,
  dirty,
  busy,
  hiddenCount,
  onAdmin,
  onQuota,
  onSections,
  onCashflow,
  isViewer,
}: Props) {
  void undoDepth;
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!downloadOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, [downloadOpen]);
  const sliders: {
    field: 'cpiY1Default' | 'cpiY2Default' | 'cpiY3Default';
    label: string;
    value: number;
  }[] = [
    { field: 'cpiY1Default', label: yearLabel(baseYear, 1), value: settings.cpiY1Default },
    { field: 'cpiY2Default', label: yearLabel(baseYear, 2), value: settings.cpiY2Default },
    { field: 'cpiY3Default', label: yearLabel(baseYear, 3), value: settings.cpiY3Default },
  ];

  return (
    <section className="panel no-print">
      <div className="panel-section">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="panel-section-title">CPI Adjustment Controls</div>
            <div className="flex flex-wrap gap-x-8 gap-y-4 mt-3">
              {sliders.map((s) => (
                <div key={s.field}>
                  <label className="block text-[11px] uppercase tracking-wider text-ccaanz-grey font-medium mb-1.5">
                    {s.label}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={10}
                      step={0.1}
                      value={s.value}
                      disabled={isViewer}
                      onChange={(e) => onSettingsChange({ [s.field]: parseFloat(e.target.value) })}
                      className="w-36 accent-ccaanz-gold disabled:opacity-50"
                    />
                    <div className="pct-input">
                      <input
                        type="number"
                        min={-5}
                        max={20}
                        step={0.1}
                        value={s.value}
                        disabled={isViewer}
                        onChange={(e) =>
                          onSettingsChange({
                            [s.field]: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="input num w-24 text-right font-medium text-ccaanz-blue disabled:opacity-50"
                      />
                      <span className="pct-suffix">%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[12px] text-ccaanz-grey mt-4 flex items-start gap-2 max-w-xl">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Per-line overrides take precedence over global rates.</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end items-center">
            <button
              type="button"
              onClick={onUndo}
              disabled={!canUndo || busy !== null}
              className="btn btn-ghost btn-sm"
              title={canUndo ? 'Undo last change (⌘Z)' : 'Nothing to undo'}
            >
              <Undo2 className="w-3.5 h-3.5" />
              Undo
            </button>
            {!isViewer && (
              <button
                type="button"
                onClick={onSave}
                disabled={!dirty || busy !== null}
                className={`btn ${dirty ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              >
                <Save className="w-3.5 h-3.5" />
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
            )}
            <div className="relative" ref={downloadRef}>
              <button
                type="button"
                onClick={() => setDownloadOpen((o) => !o)}
                disabled={busy !== null}
                className="btn btn-ghost btn-sm"
                title="Download or print — Print, Excel, CSV"
                aria-haspopup="menu"
                aria-expanded={downloadOpen}
              >
                <Download className="w-3.5 h-3.5" />
                Download
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
              {downloadOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-20 min-w-[140px] rounded-control border border-edge bg-white shadow-md overflow-hidden"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDownloadOpen(false);
                      onPrint();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] hover:bg-paper-cream"
                  >
                    <Printer className="w-3.5 h-3.5 text-ccaanz-grey" />
                    Print
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDownloadOpen(false);
                      onXlsx();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] hover:bg-paper-cream"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-ccaanz-grey" />
                    Excel (.xlsx)
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDownloadOpen(false);
                      onCsv();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] hover:bg-paper-cream"
                  >
                    <Download className="w-3.5 h-3.5 text-ccaanz-grey" />
                    CSV
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={busy !== null}
              className="btn btn-ghost btn-sm"
              title="Re-fetch from the data Sheet — discards any unsaved tool-side changes"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
            </button>

            <button
              type="button"
              onClick={() => onSettingsChange({ showHidden: !settings.showHidden })}
              className="btn btn-ghost btn-sm ml-2 relative"
              title={
                settings.showHidden
                  ? 'Hide all hidden rows'
                  : hiddenCount > 0
                    ? `Show all ${hiddenCount} hidden row${hiddenCount > 1 ? 's' : ''}`
                    : 'No hidden rows'
              }
              aria-label={settings.showHidden ? 'Hide all' : 'Show all'}
              aria-pressed={settings.showHidden}
            >
              {settings.showHidden ? (
                <Eye className="w-3.5 h-3.5" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
              Hidden
              {hiddenCount > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-px rounded-full bg-[var(--brand-lt)] text-[var(--brand)] leading-none">
                  {hiddenCount}
                </span>
              )}
            </button>

            {onSections && !isViewer && (
              <button
                type="button"
                onClick={onSections}
                className="btn btn-ghost btn-sm ml-1"
                title="Manage budget sections — add, rename, reorder, move rows between sections"
              >
                <Folders className="w-3.5 h-3.5" />
                Sections
              </button>
            )}
            {onQuota && (
              <button
                type="button"
                onClick={onQuota}
                className="btn btn-ghost btn-sm ml-1"
                title="Diocesan Quota — bind a saved quota policy to autopopulate GL 210"
              >
                <Calculator className="w-3.5 h-3.5" />
                Quota
              </button>
            )}
            {onCashflow && (
              <button
                type="button"
                onClick={onCashflow}
                className="btn btn-ghost btn-sm ml-1"
                title="Cashflow projection — rolling 12-month outlook with GST and TD timing"
              >
                <Coins className="w-3.5 h-3.5" />
                Cashflow
              </button>
            )}
            {onAdmin && (
              <button
                type="button"
                onClick={onAdmin}
                className="btn btn-ghost btn-sm ml-1"
                title="Admin — Xero connection and other settings"
              >
                <SettingsIcon className="w-3.5 h-3.5" />
                Admin
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
